import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'
import type { MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { refusalSentence } from '../refusals.js'
import { correctionTypeLabel } from '../status-labels.js'

/**
 * Correcting a record, and the log of what has already been corrected. §6.33 `COR-001`; §15.25.
 *
 * > `COR-001` `MUST` A sale, job, payment or stock movement recorded against the wrong customer,
 * > branch, staff member or job can be reassigned by a permitted role, with a reason, producing a
 * > linked correction record. The original is never deleted and **both sides show the link**.
 *
 * Both halves are here, deliberately in one panel. The log is the second half of the requirement
 * and it is not the lesser one: a sale that was moved to another customer looks, on every other
 * screen, exactly like a sale that was always against that customer. This is the only place that
 * says it was not.
 *
 * ---
 *
 * **This panel pre-checks almost nothing, and that is the design.**
 *
 * `VoidSale.tsx` sets out the rule the counter works to: every refusal the server can give must be
 * pre-checked, because a refused write sits `blocked` and holds every write queued behind it
 * (`OFF-001a`, `OFF-005`). One refused correction on a till stops the till.
 *
 * Four of the refusals here cannot be derived from anything a read sends: a closed month
 * (`ACC-008`), a settled pay period, a counted till, and a job raised on the sale. Rebuilding the
 * four queries behind them in this app is the duplication `VoidSale.tsx` names as the source of
 * three separate money defects, and it would be duplication of the worst kind: wrong in a way
 * nothing fails on, until the day the counter offers a correction the server then refuses.
 *
 * So `GET /corrections/offers` exists, and this panel renders its answer. Every correction type is
 * either offered, or shown withheld with the server's own sentence. The panel's own checks are the
 * two that are about what somebody typed rather than about the shop: a reason, and a target that
 * differs from what the field already says.
 *
 * **`VERSION_CONFLICT` is the one refusal nobody can pre-check**, and inherently so: it is
 * somebody else's write, landing between the offers read and this correction. The version comes
 * from the offers read rather than from the caller, so the two cannot disagree about which row
 * was on screen.
 *
 * ---
 *
 * **`BR-01` is pre-checked, because it can be.** The staff chooser offers only people assigned to
 * the record's own branch and the branch chooser only branches the session holds. Both are the
 * server's refusals (`STAFF_NOT_AT_BRANCH`, `BRANCH_NOT_PERMITTED`) and both are decidable here.
 *
 * **Nothing shown is an id.** `previousLabel`, `reassignedToLabel` and `currentLabel` are the
 * server's, resolved against `customer`, `staff` and `branch`, because a correction record is two
 * foreign keys and the question it answers is who a sale used to be against. An erased customer
 * reads "(no longer here)" and an undecryptable name "(name unavailable)", both from the server:
 * `ERS-00x` removes the name and the log must not be the one place it survives.
 */

// ---------------------------------------------------------------------------------------------
// What the two reads send. Shaped to `corrections.service.ts`, not imported from it: `apps/*`
// never import from each other.
// ---------------------------------------------------------------------------------------------

export interface CorrectionOfferData {
  /** Appendix E `correction_type`, unnarrowed per `ARC-020`. */
  readonly correctionType: string
  /** Which chooser this correction needs. */
  readonly target: 'customer' | 'staff' | 'branch'
  readonly currentValue: string | null
  /** The same value as a person would say it. Null when the field names nobody. */
  readonly currentLabel: string | null
  readonly offered: boolean
  /** Present only when `offered` is false. The server's sentence, shown as sent. */
  readonly code?: string
  readonly message?: string
}

export interface CorrectionOffersData {
  readonly recordId: string
  readonly branchId: string
  /** `ARC-012`'s lock, read in the same breath as the offers so the two agree. */
  readonly version: number
  readonly offers: readonly CorrectionOfferData[]
}

export interface MadeCorrectionData {
  readonly id: string
  readonly correctionType: string
  readonly previousLabel?: string | null
  readonly reassignedToLabel?: string | null
  readonly reason?: string
  readonly correctedByLabel?: string | null
  /** ISO 8601, the full instant. Which day that is at the counter is this app's to work out. */
  readonly createdAt?: string
}

/** A customer the search found. Only what the chooser shows and sends. */
export interface CorrectionCustomer {
  readonly id: string
  readonly name: string
}

/** A person who might be credited instead. `branchIds` is what makes `BR-01` pre-checkable. */
export interface CorrectionStaff {
  readonly id: string
  readonly displayName: string
  readonly branchIds: readonly string[]
  /**
   * Whether they have left. `GET /staff` returns offboarded people because `ShopSetup` lists them,
   * and `#requireTarget` refuses one with `STAFF_NOT_FOUND` **404**, which is in the write queue's
   * `REFUSED_STATUSES`. Offering somebody who has left is therefore a halted till.
   */
  readonly offboardedAt: string | null
}

export interface CorrectionsProps {
  /** `'sale'`, `'job'` or `'payment'`. Sent as given; the server decides what it corrects. */
  readonly recordType: string
  readonly recordId: string
  /**
   * What the shop calls this record, or null when it has no name yet.
   *
   * **Null rather than a placeholder.** An un-invoiced order's `number` arrives as the literal
   * English string `(not yet invoiced)`, and interpolating that gave "This moves (not yet
   * invoiced) from Bilal Ahmed to Hassan Raza", which is not a sentence. A record with no name is
   * named by what it is.
   */
  readonly recordLabel: string | null
  /** Appendix B's `Correction - reassign`. Without it the panel renders nothing at all. */
  readonly canCorrect: boolean
  readonly loadOffers: () => Promise<CorrectionOffersData>
  readonly loadMade: () => Promise<readonly MadeCorrectionData[]>
  readonly searchCustomers: (query: string) => Promise<readonly CorrectionCustomer[]>
  readonly loadStaff: () => Promise<readonly CorrectionStaff[]>
  /** The branches this session holds, which `BR-01` limits a `wrong_branch` move to. */
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  /**
   * Appendix J: the record's own branch timezone, for the one date on this panel.
   *
   * `createdAt` is the full `timestamptz` instant, deliberately, and which day that is at the
   * counter is this app's to work out. Rendering it as it arrives is the defect this run found in
   * twelve screens: `Measured Sat Oct 17`, which parses as 2001.
   */
  readonly branchTimezone: string
  /** Called once the server has taken the correction, so the record can be read again. */
  readonly onCorrected?: () => void
}

const SEARCH_FLOOR = 2

/** What to call a record that has no name of its own yet. `ARC-020`: an unknown kind falls back. */
const RECORD_WORD: Readonly<Record<string, MessageKey>> = {
  sale: 'corrections.recordSale',
  job: 'corrections.recordJob',
  payment: 'corrections.recordPayment',
}

export function Corrections({
  recordType,
  recordId,
  recordLabel,
  canCorrect,
  loadOffers,
  loadMade,
  searchCustomers,
  loadStaff,
  branches,
  branchTimezone,
  onCorrected,
}: CorrectionsProps): JSX.Element | null {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()

  const [offers, setOffers] = useState<CorrectionOffersData | null>(null)
  const [offersFailed, setOffersFailed] = useState(false)
  /**
   * Set while a re-read is out after the queue emptied, and cleared by that read.
   *
   * Not derived from `offers` changing identity: a read that answers with the same object leaves
   * the reference alone, React bails out of the render, and an effect keyed on it never runs. The
   * thing that knows the version is fresh is the read.
   */
  const [stale, setStale] = useState(false)
  const [made, setMade] = useState<readonly MadeCorrectionData[] | null>(null)
  const [madeFailed, setMadeFailed] = useState(false)

  const [chosenType, setChosenType] = useState('')
  const [target, setTarget] = useState('')
  const [targetLabel, setTargetLabel] = useState('')
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<readonly CorrectionCustomer[] | null>(null)
  const [staff, setStaff] = useState<readonly CorrectionStaff[] | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(() => {
    loadOffers().then(
      (answer) => {
        setOffers(answer)
        setOffersFailed(false)
        setStale(false)
      },
      () => {
        // Nothing is offered when the offers could not be read. Whether a month is closed is not
        // something to find out by being refused, which is what stops the till.
        setOffers(null)
        setOffersFailed(true)
        // Cleared here too: the panel offers nothing now, and leaving the "still saving" hint up
        // beside "could not check" would give two different reasons for one empty screen.
        setStale(false)
      },
    )
    loadMade().then(
      (rows) => {
        setMade(rows)
        setMadeFailed(false)
      },
      () => {
        setMade(null)
        setMadeFailed(true)
      },
    )
  }, [loadMade, loadOffers])

  useEffect(() => {
    if (!canCorrect) return
    refresh()
  }, [canCorrect, refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setChosenType('')
    setTarget('')
    setTargetLabel('')
    setQuery('')
    setMatches(null)
    setReason('')
    setConfirming(false)
    // The record itself changed, so both halves are read again: the log gains a row and the offers
    // gain a version. Re-reading the offers is what stops the next correction from being sent with
    // the version this one just superseded.
    refresh()
    onCorrected?.()
  })

  /**
   * A correction somebody discarded has to give the panel back.
   *
   * `useWriteSettled` fires only for a write the server **took**. A blocked write thrown away in
   * `BlockedWrites` never settles, so `writeId` would stay set and the panel would sit frozen with
   * no way to try again. The same defect `VoidSale.tsx` records, which cost it a void that read as
   * saved and never happened.
   */
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  const chosen = offers?.offers.find((offer) => offer.correctionType === chosenType)

  /**
   * **A correction cannot be sent while this tablet is still holding a write.** `ARC-012`.
   *
   * `expectedVersion` comes from the offers read, and `POST /orders/payments` bumps
   * `sales_order.version` when the status moves (`payments.service.ts`: `version = version + 1`
   * beside `set status`). So: take a deposit, open this panel while the payment is still queued,
   * correct the customer. The offers read answers with the pre-payment version, the correction
   * goes into the queue behind the payment, the payment lands and bumps the version, and the
   * correction is refused with a 409.
   *
   * 409 is in `REFUSED_STATUSES`, so that stops the till, and the conflicting write is this
   * tablet's own. `VERSION_CONFLICT` is inherently unpre-checkable against somebody else's write;
   * against our own it is only a matter of not sending yet.
   */
  const queueHoldsAWrite = snapshot.writes.some(
    (write) => write.status === 'pending' || write.status === 'sending',
  )

  /**
   * **And the offers are read again the moment the queue empties**, which is the other half.
   *
   * Refusing to send while a write is in flight is not enough. The write this panel is waiting out
   * is the one that changes the version, so the offers it is holding were read **before** it: the
   * payment lands, `queueHoldsAWrite` goes false, and the panel is sitting on a version that was
   * superseded two seconds ago. The correction then gets the 409 the refusal existed to prevent.
   *
   * It is not hypothetical on a Pakistani line. A payment retries while reads still succeed, the
   * shopkeeper backs out of `TakePayment` before it settles so no re-read runs, and the five
   * second drain lands it in the background.
   *
   * `stale` keeps the button down until the fresh read arrives, because the window between the
   * queue emptying and the offers coming back is exactly the window this is about.
   */
  const wasBusy = useRef(queueHoldsAWrite)
  useEffect(() => {
    if (wasBusy.current && !queueHoldsAWrite) {
      setStale(true)
      refresh()
    }
    wasBusy.current = queueHoldsAWrite
  }, [queueHoldsAWrite, refresh])
  // The invoice number where there is one, the kind of record where there is not.
  const recordWord = recordLabel ?? t(RECORD_WORD[recordType] ?? 'corrections.recordSale')

  // The staff list is read once, and only when something needs it.
  useEffect(() => {
    if (chosen?.target !== 'staff' || staff !== null) return
    loadStaff().then(setStaff, () => setStaff([]))
  }, [chosen, loadStaff, staff])

  const search = useCallback(
    (text: string) => {
      setQuery(text)
      setTarget('')
      setTargetLabel('')
      if (text.trim().length < SEARCH_FLOOR) {
        setMatches(null)
        return
      }
      searchCustomers(text.trim()).then(setMatches, () => setMatches([]))
    },
    [searchCustomers],
  )

  const pick = useCallback((id: string, label: string) => {
    setTarget(id)
    setTargetLabel(label)
    setError(null)
  }, [])

  const start = useCallback(() => {
    if (queueHoldsAWrite || stale) {
      setError(t('corrections.queueBusy'))
      return
    }
    if (reason.trim() === '') {
      // `proposeAttributionCorrection`'s first gate, and §15.25 step 2's first word.
      setError(t('corrections.reasonRequired'))
      return
    }
    if (chosen !== undefined && target === chosen.currentValue) {
      // `CORRECTION_NO_CHANGE` on the server, which is a 422 and would block the queue.
      setError(t('corrections.noChange'))
      return
    }
    setError(null)
    setConfirming(true)
  }, [chosen, queueHoldsAWrite, reason, stale, t, target])

  const submit = useCallback(async () => {
    if (offers === null || chosen === undefined) return
    setConfirming(false)
    const write = (await enqueue({
      method: 'POST',
      path: '/corrections',
      label: t('corrections.queueLabel', { record: recordWord }),
      body: {
        recordType,
        recordId,
        correctionType: chosen.correctionType,
        reason: reason.trim(),
        reassignedTo: target,
        // The version from the offers read, not from the caller: the two cannot then disagree
        // about which row was on screen when somebody decided to correct it.
        expectedVersion: offers.version,
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [chosen, enqueue, offers, recordId, recordLabel, recordType, reason, t, target])

  // Appendix B's `Correction - reassign`. A role without it gets no panel, not a disabled one.
  if (!canCorrect) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.correction'))
  const available = offers?.offers.filter((offer) => offer.offered) ?? []
  const withheld = offers?.offers.filter((offer) => !offer.offered) ?? []

  return (
    <section className="lm-corrections" aria-labelledby="corrections-heading">
      <h3 id="corrections-heading" className="lm-corrections__heading">
        {t('corrections.heading')}
      </h3>

      {/* `COR-001`'s second half, first on the panel: what this record used to say is the thing
          every other screen cannot tell you. */}
      <h4 className="lm-corrections__sub">{t('corrections.logHeading')}</h4>
      {madeFailed ? <p className="lm-corrections__failed">{t('corrections.logFailed')}</p> : null}
      {made === null && !madeFailed ? <p>{t('corrections.logLoading')}</p> : null}
      {made !== null && made.length === 0 ? <p>{t('corrections.logNone')}</p> : null}
      {made !== null && made.length > 0 ? (
        <ul className="lm-corrections__log">
          {made.map((row) => (
            <li key={row.id} className="lm-correction">
              <span className="lm-correction__what">
                {correctionTypeLabel(t, row.correctionType)}
              </span>
              <span className="lm-correction__move">
                {/* Each name goes in as an element, not as text: `DESIGN-TOKENS.md` §6 keeps a
                    proper name off the mirroring list, so in Urdu the sentence reads right to left
                    and the name inside it reads as itself. */}
                {row.previousLabel === null || row.previousLabel === undefined
                  ? sentence(t, 'corrections.movedTo', {
                      current: <Ltr>{row.reassignedToLabel ?? ''}</Ltr>,
                    })
                  : sentence(t, 'corrections.movedFrom', {
                      previous: <Ltr>{row.previousLabel}</Ltr>,
                      current: <Ltr>{row.reassignedToLabel ?? ''}</Ltr>,
                    })}
              </span>
              {row.reason === undefined ? null : (
                <span className="lm-correction__reason">{row.reason}</span>
              )}
              <span className="lm-correction__who">
                {t('corrections.correctedBy', {
                  who: row.correctedByLabel ?? '',
                  date:
                    row.createdAt === undefined
                      ? ''
                      : formatDay(row.createdAt, branchTimezone, locale),
                })}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <h4 className="lm-corrections__sub">{t('corrections.makeHeading')}</h4>
      {offersFailed ? (
        <p className="lm-corrections__failed">{t('corrections.offersFailed')}</p>
      ) : null}
      {offers === null && !offersFailed ? <p>{t('corrections.offersLoading')}</p> : null}

      {withheld.length > 0 ? (
        <>
          <p className="lm-corrections__withheld-heading">{t('corrections.withheldHeading')}</p>
          <ul className="lm-corrections__withheld">
            {withheld.map((offer) => (
              <li key={offer.correctionType}>
                <span className="lm-corrections__withheld-what">
                  {correctionTypeLabel(t, offer.correctionType)}
                </span>
                {/* The shop's words where `refusals.ts` has them, and the server's where it does
                    not: a refusal that names a month needs that month as a field before it can be
                    a catalogue sentence (`later_todo.md` 48). Either way it says the true thing. */}
                <span className="lm-corrections__withheld-why">
                  {refusalSentence(t, offer.code, offer.message)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {offers !== null && available.length === 0 ? (
        <p>{t('corrections.nothingOffered')}</p>
      ) : null}

      {available.length > 0 ? (
        <>
          <label className="lm-field">
            <span className="lm-field__label">{t('corrections.whichField')}</span>
            <select
              className="lm-input"
              value={chosenType}
              onChange={(event) => {
                setChosenType(event.target.value)
                setTarget('')
                setTargetLabel('')
                setQuery('')
                setMatches(null)
                setError(null)
              }}
            >
              <option value="">{t('corrections.fieldUnchosen')}</option>
              {available.map((offer) => (
                <option key={offer.correctionType} value={offer.correctionType}>
                  {correctionTypeLabel(t, offer.correctionType)}
                </option>
              ))}
            </select>
          </label>

          {chosen === undefined ? null : (
            <p className="lm-corrections__current">
              {chosen.currentLabel === null
                ? t('corrections.currentlyNobody')
                : sentence(t, 'corrections.currently', {
                    current: <Ltr>{chosen.currentLabel}</Ltr>,
                  })}
            </p>
          )}

          {chosen?.target === 'customer' ? (
            <label className="lm-field">
              <span className="lm-field__label">{t('corrections.chooseCustomer')}</span>
              <input
                className="lm-input"
                // A name may be typed in either script, so the box takes its direction from what
                // is in it. Without this an English name in the Urdu interface reads backwards.
                dir="auto"
                value={query}
                placeholder={t('corrections.searchCustomer')}
                onChange={(event) => search(event.target.value)}
              />
            </label>
          ) : null}
          {chosen?.target === 'customer' && query.trim().length < SEARCH_FLOOR ? (
            <p className="lm-corrections__hint">{t('corrections.searchShort')}</p>
          ) : null}
          {chosen?.target === 'customer' && matches !== null && matches.length === 0 ? (
            <p className="lm-corrections__hint">{t('corrections.searchNone')}</p>
          ) : null}
          {chosen?.target === 'customer' && matches !== null && matches.length > 0 ? (
            <ul className="lm-corrections__matches">
              {matches.map((match) => (
                <li key={match.id}>
                  <button
                    type="button"
                    className={
                      match.id === target
                        ? 'lm-corrections__match lm-corrections__match--chosen'
                        : 'lm-corrections__match'
                    }
                    onClick={() => pick(match.id, match.name)}
                  >
                    {match.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {chosen?.target === 'staff' ? (
            <StaffChooser
              t={t}
              staff={staff}
              branchId={offers?.branchId ?? ''}
              currentValue={chosen.currentValue}
              target={target}
              onPick={pick}
            />
          ) : null}
          {chosen?.target === 'branch' ? (
            <BranchChooser
              t={t}
              branches={branches}
              currentValue={chosen.currentValue}
              target={target}
              onPick={pick}
            />
          ) : null}

          {/* Said above the form, not under it: the answer is to wait, and a shopkeeper who has
              filled in three boxes first will not thank anybody for telling them then. */}
          {queueHoldsAWrite || stale ? (
            <p className="lm-corrections__hint">{t('corrections.queueBusy')}</p>
          ) : null}

          {/* The reason and the button arrive with the chooser they belong to. Asking "Why" above
              an empty "What is wrong" put the second question first, and the button sat there
              disabled with nothing to say about what it would do. */}
          {chosen === undefined ? null : (
            <>
          <label className="lm-field">
            <span className="lm-field__label">{t('corrections.reasonLabel')}</span>
            <textarea
              className="lm-input"
              dir="auto"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>

          {error === null ? null : (
            <p className="lm-corrections__error" role="alert">
              {error}
            </p>
          )}

          {confirming ? (
            <div className="lm-corrections__confirm">
              <p>
                {chosen?.currentLabel === null || chosen?.currentLabel === undefined
                  ? sentence(t, 'corrections.confirmMeansEmpty', {
                      record: recordWord,
                      next: <Ltr>{targetLabel}</Ltr>,
                    })
                  : sentence(t, 'corrections.confirmMeans', {
                      record: recordWord,
                      previous: <Ltr>{chosen.currentLabel}</Ltr>,
                      next: <Ltr>{targetLabel}</Ltr>,
                    })}
              </p>
              <button type="button" className="lm-button" onClick={submit}>
                {t('corrections.confirm')}
              </button>
              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => setConfirming(false)}
              >
                {t('corrections.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="lm-button"
              disabled={target === '' || writeId !== null || queueHoldsAWrite || stale}
              onClick={start}
            >
              {t('corrections.start')}
            </button>
          )}
            </>
          )}
        </>
      ) : null}

      {line === null ? null : <p className="lm-corrections__queue">{line}</p>}
    </section>
  )
}

/**
 * `BR-01` and `ROLE-001`, pre-checked rather than discovered.
 *
 * Three people the server would refuse, each with a status the write queue treats as final:
 * somebody who does not work at this record's branch (`STAFF_NOT_AT_BRANCH`, 422), somebody who has
 * left (`STAFF_NOT_FOUND`, 404) and the person the field already names (`CORRECTION_NO_CHANGE`,
 * 422). All three are decidable here, so the chooser simply does not offer them.
 *
 * **Declared at module scope, not inside `Corrections`.** A component defined in a render body is a
 * new component type on every render, so React unmounts and remounts it: the `<select>` loses focus
 * on the change it fired itself. On Windows Chrome an arrow key fires `onChange`, so a keyboard
 * user would get one step down the staff list and be dropped out of the control.
 * `userEvent.selectOptions` sets the value directly and cannot see that at all.
 */
function StaffChooser({
  t,
  staff,
  branchId,
  currentValue,
  target,
  onPick,
}: {
  readonly t: ReturnType<typeof useTranslation>['t']
  readonly staff: readonly CorrectionStaff[] | null
  readonly branchId: string
  readonly currentValue: string | null
  readonly target: string
  readonly onPick: (id: string, label: string) => void
}): JSX.Element {
  const here = (staff ?? []).filter(
    (person) =>
      person.id !== currentValue &&
      person.offboardedAt === null &&
      person.branchIds.includes(branchId),
  )
  if (staff !== null && here.length === 0) {
    return <p className="lm-corrections__hint">{t('corrections.noStaff')}</p>
  }
  return (
    <label className="lm-field">
      <span className="lm-field__label">{t('corrections.chooseStaff')}</span>
      <select
        className="lm-input"
        value={target}
        onChange={(event) => {
          const found = here.find((person) => person.id === event.target.value)
          onPick(event.target.value, found?.displayName ?? '')
        }}
      >
        <option value="">{t('corrections.fieldUnchosen')}</option>
        {here.map((person) => (
          <option key={person.id} value={person.id}>
            {person.displayName}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * The branches this session holds, which is the same `BR-01` check the server makes on both ends.
 *
 * No closed-branch filter, because there is nothing to filter: `session-context.service.ts` reads
 * `from branch where ... and closed_at is null`, so a closed branch never reaches this prop and
 * `BRANCH_NOT_FOUND` is unreachable from here. Stated rather than left to be rediscovered.
 */
function BranchChooser({
  t,
  branches,
  currentValue,
  target,
  onPick,
}: {
  readonly t: ReturnType<typeof useTranslation>['t']
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  readonly currentValue: string | null
  readonly target: string
  readonly onPick: (id: string, label: string) => void
}): JSX.Element {
  const elsewhere = branches.filter((branch) => branch.id !== currentValue)
  if (elsewhere.length === 0) {
    return <p className="lm-corrections__hint">{t('corrections.noBranch')}</p>
  }
  return (
    <label className="lm-field">
      <span className="lm-field__label">{t('corrections.chooseBranch')}</span>
      <select
        className="lm-input"
        value={target}
        onChange={(event) => {
          const found = elsewhere.find((branch) => branch.id === event.target.value)
          onPick(event.target.value, found?.name ?? '')
        }}
      >
        <option value="">{t('corrections.fieldUnchosen')}</option>
        {elsewhere.map((branch) => (
          <option key={branch.id} value={branch.id}>
            {branch.name}
          </option>
        ))}
      </select>
    </label>
  )
}
